import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useControlPlaneState } from "./controlPlane";
import {
  h3Policy,
  h3ReaderStatus,
  previewH3Limits,
  publishH3Limits,
  type H3Limits,
} from "./h3Control";
import type { ControlPlanePayload } from "../runtime-config/contracts";

function H3LimitsEditor({
  payload,
  refresh,
}: {
  payload: ControlPlanePayload;
  refresh: () => Promise<unknown>;
}) {
  const policy = h3Policy(payload);
  const [limits, setLimits] = useState<H3Limits | null>(policy);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  if (!policy || !limits)
    return <p>H3 policy has not been published in this environment.</p>;
  let validation = "";
  try {
    previewH3Limits(payload, limits);
  } catch (error) {
    validation = error instanceof Error ? error.message : "Invalid policy";
  }
  const changed = Object.keys(limits).some(
    (key) => key in policy && limits[key as keyof H3Limits] !== policy[key],
  );
  const labels: Record<keyof H3Limits, string> = {
    submission_concurrency: "Concurrent scene submissions",
    account_outstanding_limit: "Outstanding fal requests",
    maximum_generation_seconds: "Maximum generated seconds per scene",
  };
  return (
    <div className="space-y-3">
      <p>H3 execution providers: {policy.allowed_destinations.join(", ")}</p>
      <p className="text-sm">
        Prompt expansion: {String(policy.prompt_expansion_mode)}. Reference
        contract: {String(policy.prompt_format)}.
      </p>
      <div className="flex flex-wrap gap-2">
        {h3ReaderStatus(payload).map((reader) => (
          <Badge key={reader.service} variant="outline">
            {reader.service}:{" "}
            {reader.reported
              ? "matching readback reported"
              : "readback pending or stale"}
          </Badge>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Service reports do not certify a successful Echo. Check the journey and
        selected scene receipts.
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        {(Object.keys(labels) as (keyof H3Limits)[]).map((key) => (
          <label key={key} className="text-sm">
            {labels[key]}
            <Input
              aria-label={labels[key]}
              type="number"
              value={limits[key]}
              disabled={saving || payload.environment !== "staging"}
              onChange={(event) =>
                setLimits({ ...limits, [key]: Number(event.target.value) })
              }
            />
            <span className="text-xs text-muted-foreground">
              Published: {policy[key]}
            </span>
          </label>
        ))}
      </div>
      <p className="text-sm">
        Preview: only these three H3 limits change for new Echoes. In-flight
        Echoes retain their frozen release. Lower account limits after existing
        requests drain; higher limits require fal account capacity. Scenes above
        the duration limit require replanning.
      </p>
      {validation ? (
        <p className="text-sm text-amber-700">{validation}</p>
      ) : null}
      <Input
        aria-label="H3 change reason"
        placeholder="Reason for this H3 configuration change"
        value={reason}
        disabled={saving}
        onChange={(event) => setReason(event.target.value)}
      />
      <Button
        disabled={
          saving || !!validation || !changed || reason.trim().length < 10
        }
        onClick={async () => {
          setSaving(true);
          setMessage("");
          try {
            const release = await publishH3Limits(payload, limits, reason);
            setMessage(
              `Published release ${release.id}. Service readback and Echo verification are pending.`,
            );
            await refresh();
          } catch (error) {
            setMessage(
              error instanceof Error ? error.message : "Publication failed",
            );
          } finally {
            setSaving(false);
          }
        }}
      >
        {saving ? "Publishing…" : "Publish reviewed H3 limits"}
      </Button>
      {message ? (
        <p role="status" className="text-sm">
          {message}
        </p>
      ) : null}
    </div>
  );
}

export function H3ControlCard({
  environment,
}: {
  environment: "staging" | "stg";
}) {
  const { state, isLoading, refresh } = useControlPlaneState();
  const payload = state?.status === "ok" ? state.payload : null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>H3 execution configuration</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p>
          {environment === "staging"
            ? "Engineering staging (staging)"
            : "Customer-facing production (stg)"}
        </p>
        {isLoading ? <p>Reading RDS configuration…</p> : null}
        {!isLoading && !payload ? (
          <p>
            H3 control-plane state is unavailable. No edits can be published.
          </p>
        ) : null}
        {payload && payload.environment !== environment ? (
          <p>
            This CRM control-plane connection belongs to{" "}
            {payload.environment === "stg"
              ? "production (stg)"
              : "engineering staging (staging)"}
            . Open that environment's CRM to edit its settings.
          </p>
        ) : null}
        {payload && payload.environment === environment ? (
          <>
            <p className="break-all font-mono text-xs">
              Desired release: {payload.active_release?.id ?? "none"}
            </p>
            <H3LimitsEditor
              key={payload.active_release?.id ?? "none"}
              payload={payload}
              refresh={refresh}
            />
          </>
        ) : null}
        <Button variant="outline" onClick={() => void refresh()}>
          Refresh H3 readbacks
        </Button>
      </CardContent>
    </Card>
  );
}
